import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'dayjs/locale/en'
import { useTranslation } from 'react-i18next'
import { PlusCircleOutlined, CalendarOutlined } from '@ant-design/icons'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { Empty } from 'antd'
import { SelectButton } from '../select-button'
import { Wrapper, EmptyWrapper, Item } from './styled'
import { STATUS, TASK_CATEGORY_VALUE } from '../../constants'
import { useHistories, useProfile } from '../../../../hooks'
import useQuery from '../../../../hooks/useQuery'
import { ROUTES_NAME } from '../../../../constants'

const getStatusClassName = (status) => {
  switch (status) {
    case STATUS.NOT_STARTED:
      return 'not-started'
    case STATUS.IN_PROGRESS:
      return 'in-progress'
    case STATUS.COMPLETED:
      return 'completed'
    default:
      return ''
  }
}

export const TaskList = ({ t, items, setItems }) => {
  const { i18n: { language } } = useTranslation()
  const { profile: { userId } } = useProfile()
  // const { updateTaskOrderAction } = useTaskManagement()
  const [locale, setLocale] = useState('ja')
  const query = useQuery()
  const taskCategoryId = query.get('taskCategoryId')
  const isDraggable = !taskCategoryId || Number(taskCategoryId) === userId

  useEffect(() => {
    setLocale(language === 'jp' ? 'ja' : 'en')
  }, [language])

  const onDragEnd = (result) => {
    if (!result.destination) return

    const reorderedItems = Array.from(items)
    const [removed] = reorderedItems.splice(result.source.index, 1)
    reorderedItems.splice(result.destination.index, 0, removed)

    setItems(reorderedItems)
    // updateTaskOrderAction(reorderedItems.map(({ id }, index) => ({ taskId: id, displayOrder: index })))
  }

  if (items.length === 0) {
    return (
      <EmptyWrapper>
        <Empty description={t('task.no_task')} />
      </EmptyWrapper>
    )
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <Wrapper
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {items.map((item, index) => (
              <Draggable key={item.id} draggableId={item.id.toString()} index={index} isDragDisabled={!isDraggable}>
                {(providedDraggable) => (
                  <div
                    ref={providedDraggable.innerRef}
                    {...providedDraggable.draggableProps}
                    {...providedDraggable.dragHandleProps}
                    style={{
                      ...providedDraggable.draggableProps.style,
                      marginBottom: 16
                    }}
                  >
                    <TaskItem item={item} setItems={setItems} isDraggable={isDraggable} locale={locale} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Wrapper>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export const TaskItem = React.memo(({
  item,
  item: { id, title, dueDateStr, assignees, requesterName, isRequesterMe, targetValue, valueUnit, achievementValue, memo, statusName, selected },
  setItems,
  isDraggable,
  locale
}) => {
  const { t } = useTranslation()
  const history = useHistories()
  const { profile: { userId } } = useProfile()
  const query = useQuery()
  const taskTypeId = query.get('taskTypeId') || TASK_CATEGORY_VALUE.MY_TASK
  const taskCategoryId = query.get('taskCategoryId') || userId

  const handleSelect = () => {
    setItems((prev) => prev.map((_item) => (_item.id === id ? { ..._item, selected: !selected } : _item)))
  }

  const handleClick = () => {
    history.push(`${ROUTES_NAME.EDIT_TASK}/${id}?taskTypeId=${taskTypeId}&taskCategoryId=${taskCategoryId}`, { task: item })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleClick()
    }
  }

  return (
    <Item>
      {isRequesterMe && <SelectButton selected={selected} onClick={handleSelect} size={25} />}

      {/** draggable にならないため、ここでは <button> は使わない  */}
      <div className="item-content" onClick={handleClick} tabIndex={0} role="button" onKeyDown={handleKeyDown} style={{ cursor: isDraggable ? 'grab' : 'pointer' }}>
        <div className="title">{title}</div>
        <div className="requester-assignees">
          <span>{requesterName}</span>
          <span style={{ fontSize: 9 }}>▶</span>
          <Assignees t={t} assignees={assignees} />
        </div>
        <div className="due-date">
          <CalendarOutlined />
          {dayjs(dueDateStr).locale(locale).format('YYYY/MM/DD(ddd) HH:mm')}
        </div>
        <div className={`status ${getStatusClassName(statusName)}`}>{t(`task.${statusName}`)}</div>
        <div className="target-achievement">
          <div>{achievementValue}/{targetValue}<span>{valueUnit}</span></div>
          <div>
            {targetValue !== 0 && Math.round((achievementValue / targetValue) * 100)}
            {targetValue !== 0 && <span>%</span>}
          </div>
        </div>
        {memo && <div className="memo">{memo}</div>}
      </div>
    </Item>
  )
})

export const Assignees = React.memo(({ t, assignees }) => {
  if (assignees.length === 0) {
    return (
      <span>{t('task.none')}</span>
    )
  }

  if (assignees.length === 1) {
    return (
      <span>{assignees[0].name}</span>
    )
  }

  return (
    <span>
      {assignees[0].name}{' '}<PlusCircleOutlined />
    </span>
  )
})

TaskList.propTypes = {
  items: PropTypes.array,
  setItems: PropTypes.func
}

TaskItem.propTypes = {
  item: PropTypes.object,
  setItems: PropTypes.func,
  isDraggable: PropTypes.bool,
  locale: PropTypes.string
}

Assignees.propTypes = {
  assignees: PropTypes.array
}
